// Lematyzacja angielska na potrzeby kontroli treści lekcji: z formy ze zdania („drinks”, „bigger”, „children”) do haseł,
// które mogą za nią stać. Nie jest to pełny analizator — zwraca KANDYDATÓW, a ostateczną decyzję podejmuje słownik
// (src/lib/cefr.ts) razem z częścią mowy: „hated” → „hate” (czasownik), a nie „hat” (rzeczownik, bo ten nie ma formy -ed).
// Nieznane słowo zostaje nierozpoznane i linter każe autorowi je zmienić — lepiej odrzucić niż przepuścić.

export type FormKind = 'base' | 'irregular' | 'plural-or-third' | 'past' | 'ing' | 'comparative' | 'superlative' | 'adverb';
/** Część mowy hasła, jakiej wymaga dana forma (rzeczownik w liczbie mnogiej, czasownik w -ing itd.). */
export type Gate = 'noun' | 'verb' | 'adjective' | 'adverb';

export interface Candidate {
  lemma: string;
  kind: FormKind;
  /** Hasło musi mieć jedną z tych części mowy; brak = dowolna. */
  gate?: readonly Gate[];
}

/** Czasowniki nieregularne: hasło → wszystkie jego formy odmienione (także -s i -ing, gdy nieregularne). */
const IRREGULAR_VERBS = `
be: am is are was were been being
have: has had having
do: does did done doing
go: goes went gone going
say: says said
get: gets got gotten getting
make: makes made making
know: knows knew known
think: thinks thought
take: takes took taken taking
see: sees saw seen seeing
come: comes came coming
give: gives gave given giving
find: finds found
tell: tells told
become: becomes became becoming
leave: leaves left leaving
feel: feels felt
put: puts putting
bring: brings brought
begin: begins began begun beginning
keep: keeps kept
hold: holds held
write: writes wrote written writing
stand: stands stood
hear: hears heard
let: lets letting
mean: means meant
set: sets setting
meet: meets met
run: runs ran running
pay: pays paid
sit: sits sat sitting
speak: speaks spoke spoken
lie: lies lay lain lying
lead: leads led
read: reads
grow: grows grew grown
lose: loses lost losing
fall: falls fell fallen
send: sends sent
build: builds built
understand: understands understood
draw: draws drew drawn
break: breaks broke broken
spend: spends spent
cut: cuts cutting
rise: rises rose risen
drive: drives drove driven driving
buy: buys bought
wear: wears wore worn
choose: chooses chose chosen
sing: sings sang sung
eat: eats ate eaten eating
drink: drinks drank drunk
swim: swims swam swum swimming
fly: flies flew flown
ride: rides rode ridden riding
sleep: sleeps slept
wake: wakes woke woken
win: wins won winning
forget: forgets forgot forgotten
hide: hides hid hidden
throw: throws threw thrown
catch: catches caught
teach: teaches taught
sell: sells sold
shake: shakes shook shaken
shoot: shoots shot
steal: steals stole stolen
bite: bites bit bitten
blow: blows blew blown
burn: burns burnt
dig: digs dug digging
feed: feeds fed
fight: fights fought
freeze: freezes froze frozen
hang: hangs hung
hit: hits hitting
hurt: hurts
kneel: knelt
lay: lays laid
lend: lends lent
light: lights lit
ring: rings rang rung
shut: shuts shutting
sink: sinks sank sunk
slide: slides slid
spin: spins spun
split: splits
spread: spreads
stick: sticks stuck
strike: strikes struck
swing: swings swung
tear: tears tore torn
wind: winds wound
bear: bears bore borne
beat: beats beaten
bend: bends bent
bet: bets betting
bind: binds bound
bleed: bleeds bled
breed: breeds bred
cost: costs
deal: deals dealt
forgive: forgives forgave forgiven
learn: learns learnt
leap: leaps leapt
seek: seeks sought
spell: spells spelt
smell: smells smelt
dream: dreams dreamt
swear: swears swore sworn
weave: weaves wove woven
overcome: overcomes overcame
undo: undoes undid undone
rebuild: rebuilds rebuilt
rewrite: rewrites rewrote rewritten
withdraw: withdraws withdrew withdrawn
upset: upsets
quit: quits
shine: shines shone
sew: sews sewn
lean: leans leant
show: shows shown
prove: proves proven
shave: shaves shaven
mow: mows mown
sow: sows sown
swell: swells swollen
saw: saws sawn
`;

/** Rzeczowniki z nieregularną liczbą mnogą: forma → hasło. */
const IRREGULAR_NOUNS: Record<string, string> = {
  children: 'child', men: 'man', women: 'woman', people: 'person', feet: 'foot', teeth: 'tooth', mice: 'mouse', geese: 'goose',
  lives: 'life', wives: 'wife', knives: 'knife', leaves: 'leaf', loaves: 'loaf', shelves: 'shelf', thieves: 'thief', halves: 'half',
  wolves: 'wolf', calves: 'calf', selves: 'self', potatoes: 'potato', tomatoes: 'tomato', heroes: 'hero', echoes: 'echo', oxen: 'ox',
  dice: 'die', crises: 'crisis', analyses: 'analysis', theses: 'thesis', phenomena: 'phenomenon', criteria: 'criterion', media: 'medium',
  bacteria: 'bacterium', cacti: 'cactus', fungi: 'fungus', nuclei: 'nucleus', stimuli: 'stimulus', alumni: 'alumnus',
};

/** Stopniowanie nieregularne: forma → [hasło, rodzaj]. */
const IRREGULAR_DEGREE: Record<string, [string, 'comparative' | 'superlative']> = {
  better: ['good', 'comparative'], best: ['good', 'superlative'],
  worse: ['bad', 'comparative'], worst: ['bad', 'superlative'],
  further: ['far', 'comparative'], furthest: ['far', 'superlative'],
  farther: ['far', 'comparative'], farthest: ['far', 'superlative'],
  elder: ['old', 'comparative'], eldest: ['old', 'superlative'],
  less: ['little', 'comparative'], least: ['little', 'superlative'],
  more: ['much', 'comparative'], most: ['much', 'superlative'],
};
const IRREGULAR_DEGREE_ALT: Record<string, string[]> = {
  better: ['well'], best: ['well'], worse: ['badly', 'ill'], worst: ['badly', 'ill'], more: ['many'], most: ['many'],
};

const VERB_FORMS: Map<string, string[]> = (() => {
  const m = new Map<string, string[]>();
  for (const line of IRREGULAR_VERBS.split('\n')) {
    const [lemma, forms] = line.split(':').map((s) => s.trim());
    if (!lemma || !forms) continue;
    for (const f of forms.split(/\s+/)) {
      const list = m.get(f) ?? [];
      if (!list.includes(lemma)) list.push(lemma);
      m.set(f, list);
    }
  }
  return m;
})();

const VOWELS = 'aeiou';
const isConsonant = (c: string | undefined): boolean => c !== undefined && /[a-z]/.test(c) && !VOWELS.includes(c);

/** Stem po odcięciu końcówki: wersje „goła”, z -e, z odwojonym spółgłoskowym, -y→-i. Minimalna długość rdzenia: 2. */
function stems(stem: string): string[] {
  const out: string[] = [];
  if (stem.length >= 2) out.push(stem);
  if (stem.length >= 2) out.push(`${stem}e`);
  const n = stem.length;
  if (n >= 3 && stem[n - 1] === stem[n - 2] && isConsonant(stem[n - 1])) out.push(stem.slice(0, -1));
  return out;
}

/**
 * Kandydaci na hasło dla jednej formy (małe litery, bez apostrofu). Pierwszy zawsze to sama forma.
 * Kolejność: forma sama → nieregularne → regularne końcówki. Duplikaty usunięte.
 */
export function lemmaCandidates(token: string): Candidate[] {
  const w = token.toLowerCase();
  const out: Candidate[] = [{ lemma: w, kind: 'base' }];
  const seen = new Set<string>([`${w}|base`]);
  const add = (lemma: string, kind: FormKind, gate?: readonly Gate[]): void => {
    if (lemma.length < 2 && lemma !== 'a' && lemma !== 'i') return;
    const key = `${lemma}|${kind}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(gate ? { lemma, kind, gate } : { lemma, kind });
  };

  for (const lemma of VERB_FORMS.get(w) ?? []) add(lemma, 'irregular', ['verb']);
  if (IRREGULAR_NOUNS[w]) add(IRREGULAR_NOUNS[w], 'irregular', ['noun']);
  const deg = IRREGULAR_DEGREE[w];
  if (deg) {
    add(deg[0], deg[1], ['adjective', 'adverb']);
    for (const alt of IRREGULAR_DEGREE_ALT[w] ?? []) add(alt, deg[1], ['adjective', 'adverb']);
  }

  // rzeczownik w liczbie mnogiej / czasownik w 3. osobie
  if (w.length >= 3 && w.endsWith('s') && !w.endsWith('ss')) {
    const S: readonly Gate[] = ['noun', 'verb'];
    if (w.endsWith('ies') && w.length > 4) add(`${w.slice(0, -3)}y`, 'plural-or-third', S);
    if (w.endsWith('es')) add(w.slice(0, -2), 'plural-or-third', S);
    add(w.slice(0, -1), 'plural-or-third', S);
  }
  // -ing
  if (w.length >= 5 && w.endsWith('ing')) {
    const stem = w.slice(0, -3);
    for (const s of stems(stem)) add(s, 'ing', ['verb']);
    if (stem.endsWith('y')) add(`${stem.slice(0, -1)}ie`, 'ing', ['verb']);
  }
  // -ed
  if (w.length >= 4 && w.endsWith('ed')) {
    for (const s of stems(w.slice(0, -2))) add(s, 'past', ['verb']);
    add(w.slice(0, -1), 'past', ['verb']);
    if (w.endsWith('ied')) add(`${w.slice(0, -3)}y`, 'past', ['verb']);
  }
  // -er / -est
  if (w.length >= 4 && w.endsWith('er')) {
    for (const s of stems(w.slice(0, -2))) add(s, 'comparative', ['adjective', 'adverb']);
    add(w.slice(0, -1), 'comparative', ['adjective', 'adverb']);
    if (w.endsWith('ier')) add(`${w.slice(0, -3)}y`, 'comparative', ['adjective', 'adverb']);
  }
  if (w.length >= 5 && w.endsWith('est')) {
    for (const s of stems(w.slice(0, -3))) add(s, 'superlative', ['adjective', 'adverb']);
    add(w.slice(0, -2), 'superlative', ['adjective', 'adverb']);
    if (w.endsWith('iest')) add(`${w.slice(0, -4)}y`, 'superlative', ['adjective', 'adverb']);
  }
  // -ly
  if (w.length >= 5 && w.endsWith('ly')) {
    add(w.slice(0, -2), 'adverb', ['adjective']);
    if (w.endsWith('ily')) add(`${w.slice(0, -3)}y`, 'adverb', ['adjective']);
    if (w.endsWith('bly')) add(`${w.slice(0, -1)}e`, 'adverb', ['adjective']);
    if (w.endsWith('ally')) add(w.slice(0, -4), 'adverb', ['adjective']);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Tokeny i skróty
// ---------------------------------------------------------------------------------------------

export interface Token {
  /** Forma ze zdania, z oryginalną wielkością liter (apostrof ’ zamieniony na '). */
  raw: string;
  lower: string;
  /** Początek znaku w tekście. */
  index: number;
  capitalized: boolean;
  /** Pierwsze słowo zdania (po kropce, ?, ! albo początek tekstu). */
  sentenceStart: boolean;
}

/** Dzieli tekst na słowa (litery, z apostrofem lub łącznikiem wewnątrz: „don't”, „o'clock”, „t-shirt”). Liczby i znaki przestankowe są pomijane. */
export function tokenize(text: string): Token[] {
  const out: Token[] = [];
  const re = /[A-Za-z]+(?:['’-][A-Za-z]+)*/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const raw = m[0].replace(/’/g, "'");
    const before = text.slice(0, m.index).replace(/[\s"'“”‘’(\[]+$/u, '');
    const sentenceStart = before === '' || /[.!?…]$/u.test(before);
    out.push({ raw, lower: raw.toLowerCase(), index: m.index, capitalized: /^[A-Z]/.test(raw), sentenceStart });
  }
  return out;
}

/** Formy nieregularnych czasowników, które odpowiadają tej formie (puste, gdy to nie forma nieregularna). */
export function irregularVerbLemmas(token: string): readonly string[] {
  return VERB_FORMS.get(token.toLowerCase()) ?? [];
}

const IS_CONTRACTIBLE = new Set(['it', 'that', 'what', 'he', 'she', 'there', 'here', 'who', 'where', 'how', 'when', 'why', 'this', 'everyone', 'someone', 'nobody', 'everything', 'something', 'nothing']);
const NT: Record<string, string[]> = {
  "can't": ['can', 'not'], "won't": ['will', 'not'], "shan't": ['shall', 'not'], "ain't": ['be', 'not'],
};

/**
 * Rozwija skrót na słowa składowe („it's” → it, is; „don't” → do, not; „Tom's” → tom). Zwraca null, gdy to nie skrót.
 * Dzierżawczy 's po rzeczowniku/imieniu zostawia samą podstawę.
 */
export function expandContraction(token: string): string[] | null {
  const w = token.toLowerCase().replace(/’/g, "'");
  if (!w.includes("'")) return null;
  if (NT[w]) return NT[w];
  if (w.endsWith("n't") && w.length > 3) return [w.slice(0, -3), 'not'];
  const m = /^([a-z]+)'(s|re|m|ll|d|ve)$/.exec(w);
  if (!m) return null;
  const [, base, suffix] = m as unknown as [string, string, string];
  switch (suffix) {
    case 's':
      return base === 'let' ? ['let', 'us'] : IS_CONTRACTIBLE.has(base) ? [base, 'is'] : [base];
    case 're':
      return [base, 'are'];
    case 'm':
      return [base, 'am'];
    case 'll':
      return [base, 'will'];
    case 'd':
      return [base, 'would'];
    case 've':
      return [base, 'have'];
  }
  return null;
}
